import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil, sortBy } from 'lodash'

import { Button } from '@/components/buttons/Button'
import NoData from '@/components/NoData'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { AnimatedHeight } from '@/components/layout/AnimatedHeight'
import { Loader } from '@/components/loaders/Loader'
import { fetchPlaybooksByOrg } from '@/reducers/analytics/analytics.actions'
import { ConnectionList } from '@/views/Organizations/components/ConnectionList'
import { OrgSyncPausedBanner } from '@/views/Organizations/components/OrgSyncPausedBanner'
import { useConnectionListPanel } from '@/views/Organizations/components/hooks/useConnectionListPanel'
import {
  KNOWLEDGE_BASE_PROVIDERS,
  providerOf,
} from '@/views/Organizations/components/knowledgeBaseProviders'
import { REVIEW_STATE } from '@/views/Organizations/components/ReviewFilesModal.helpers'
import { ScanningModal } from '@/views/Organizations/components/ScanningModal'
import {
  readDismissedNotebooks,
  writeDismissedNotebooks,
} from '@/views/Organizations/components/unindexedNotebooks.helpers'

import './KnowledgeBaseConnections.scss'

// One list for every provider's connections. The responses carry no provider
// discriminator, so each row is tagged client-side with the provider it was
// fetched through. All providers share one backing table, so ids are unique
// across the merged list and sorting by id is global creation order — stable,
// so rows keep their position across a save instead of reshuffling under the
// operator's cursor.
//
// All-or-nothing on purpose: Promise.all rejects if EITHER provider's fetch
// rejects, and every provider thunk rethrows load errors for exactly that
// reason. A half list is not a smaller truth, it is a wrong one — the panel
// presents it as "the org's connections", and the destructive surfaces count
// it: the erase confirmation tells the operator how many sibling connections
// survive the purge, so a provider silently missing from the list would
// under-count them on the one screen where that number has to be right.
const fetchKnowledgeBaseConnections =
  ({ organizationId }) =>
  async (dispatch) => {
    const rowsByProvider = await Promise.all(
      KNOWLEDGE_BASE_PROVIDERS.map(async (provider) => {
        const rows = await dispatch(provider.fetchConnections({ organizationId }))
        return rows.map((row) => ({ ...row, provider: provider.key }))
      })
    )
    return sortBy(rowsByProvider.flat(), 'id')
  }

const fetchKnowledgeBaseSyncState = ({ connection }) =>
  providerOf(connection.provider).fetchSyncState({ connectionId: connection.id })

/**
 * The Manage Knowledge Base panel: every connection the org holds across all
 * knowledge-base providers in one list, then the selected one's detail. The
 * provider registry (knowledgeBaseProviders.js) supplies everything
 * provider-specific — icon, names, thunks, detail form, erase confirmation —
 * so a new provider is one registry entry, not a new panel.
 *
 * @param {number} props.organizationId the org being configured
 * @param {Function} props.onClose closes the modal (routed through the
 *   unsaved-changes guard by ButtonAndFormModal)
 * @param {Function} props.onDirtyChange reports unsaved input — the mounted
 *   form's, or a preserved draft's — to the hosting modal's close guard
 *   (injected by ButtonAndFormModal)
 */
export const KnowledgeBaseConnectionPanel = ({ organizationId, onClose, onDirtyChange }) => {
  const dispatch = useDispatch()

  // QCER-1075: the per-row playbook scope needs this org's playbooks. Fetched
  // here rather than per form so switching connections does not re-request
  // them, and shared by both providers' allowlists. The same action and the
  // same {value: cid, label: name} shape the AI Answers upload form uses.
  const { data, loading } = useSelector((state) => state.analytics)
  const { playbooks: fetchedPlaybooks, fetchedPlaybooksOrg } = data
  // The analytics slice is SHARED with other pages, and one of them fills it
  // with a grouped, multi-org shape. Until it holds this org's options, treat
  // it as still loading: showing another organization's playbooks in a control
  // whose whole job is client separation is the one thing it must not do.
  const playbooksAreThisOrgs = fetchedPlaybooksOrg === organizationId
  const playbooks = playbooksAreThisOrgs ? fetchedPlaybooks : []
  // A failed fetch never sets `fetchedPlaybooksOrg`, so without tracking it the
  // control would spin forever — indistinguishable from a slow load, and with
  // the toast long gone by the time anyone opens the form. A refetch that fails
  // on top of options already held is not worth surfacing; only a control that
  // will never fill is.
  const [fetchFailed, setFetchFailed] = useState(false)
  const playbooksFailed = fetchFailed && !playbooksAreThisOrgs
  const playbooksLoading = !playbooksFailed && (loading.playbooks || !playbooksAreThisOrgs)
  useEffect(() => {
    // Skipped when the slice already holds THIS org's options: the panel
    // remounts on every open, and the org's playbooks do not change between
    // them. Matches how the shared slice is filled elsewhere
    // (`fetchAnalyticsFilters`, and `AnalyticsFilters` since #11307).
    if (playbooksAreThisOrgs) {
      return undefined
    }
    let current = true
    setFetchFailed(false)
    dispatch(fetchPlaybooksByOrg(organizationId)).then((options) => {
      if (current) {
        setFetchFailed(isNil(options))
      }
    })
    return () => {
      current = false
    }
  }, [dispatch, organizationId, playbooksAreThisOrgs])

  const {
    connections,
    connection,
    selectedId,
    selectConnection,
    creating,
    startCreate,
    cancelCreate,
    syncState,
    loaded,
    loadError,
    reload,
  } = useConnectionListPanel({
    organizationId,
    fetchConnections: fetchKnowledgeBaseConnections,
    fetchSyncState: fetchKnowledgeBaseSyncState,
  })
  // Which provider the create form is for. Set right before startCreate() and
  // only read while `creating`, so staleness after a cancel or row-select is
  // harmless.
  const [createProvider, setCreateProvider] = useState(null)
  // The connection whose erase confirmation is open; null = none.
  const [eraseTarget, setEraseTarget] = useState(null)
  // The connection just created that starts with a scan; null = no notice.
  const [scanNoticeId, setScanNoticeId] = useState(null)
  const [purging, setPurging] = useState(false)
  // Dismissed notebook keys by connection id: the one copy the rail's counts and the
  // form's banner both read. Each connection reads storage once, when it is first listed.
  const [dismissedNotebooks, setDismissedNotebooks] = useState({})
  const unreadConnections = connections.filter((row) => isNil(dismissedNotebooks[row.id]))
  if (!isEmpty(unreadConnections)) {
    // Set during render, so React renders again before committing and no frame misses a dismissal.
    setDismissedNotebooks({
      ...dismissedNotebooks,
      ...Object.fromEntries(
        unreadConnections.map((row) => [row.id, readDismissedNotebooks(row.id)])
      ),
    })
  }
  // Unsaved create drafts, keyed by provider: the typed values of create forms
  // the operator switched away from, preserved so navigating the list never
  // destroys input. Cleared by Cancel, by a successful save, or with the
  // modal. State drives the rail's draft rows; the ref mirror exists because
  // the values arrive from Formik's render, where reading fresh state is not
  // an option.
  const [drafts, setDrafts] = useState({})
  const draftsRef = useRef({})
  // The mounted detail form's live Formik state, mirrored every render so a
  // draft's values can still be captured at the moment the form unmounts.
  const formValuesRef = useRef(null)
  const formDirtyRef = useRef(false)

  // The modal's unsaved-changes guard covers typed-but-unsaved input anywhere
  // in the panel: the mounted form, or a preserved draft.
  const reportDirty = () => onDirtyChange?.(formDirtyRef.current || !isEmpty(draftsRef.current))

  const handleFormDirty = (dirty) => {
    formDirtyRef.current = dirty
    reportDirty()
  }

  const handleFormikState = (formik) => {
    formValuesRef.current = formik.values
  }

  // Written through to storage, so a dismissal outlives the modal.
  const changeDismissedNotebooks = (connectionId, keys) => {
    writeDismissedNotebooks(connectionId, keys)
    setDismissedNotebooks((current) => ({ ...current, [connectionId]: keys }))
  }

  const setDraftsEverywhere = (nextDrafts) => {
    draftsRef.current = nextDrafts
    setDrafts(nextDrafts)
    reportDirty()
  }

  const removeDraft = (providerKey) => {
    const { [providerKey]: removed, ...remaining } = draftsRef.current
    setDraftsEverywhere(remaining)
  }

  // Keep the active create form's input when the operator typed anything (or
  // is revisiting an already-preserved draft) before navigating away from it.
  // An untouched blank form is not worth a rail row.
  const captureActiveDraft = () => {
    if (!creating) {
      return
    }
    if (formDirtyRef.current || !isNil(draftsRef.current[createProvider])) {
      setDraftsEverywhere({ ...draftsRef.current, [createProvider]: formValuesRef.current })
    }
  }

  const openDraft = (providerKey) => {
    if (creating && createProvider === providerKey) {
      return
    }
    captureActiveDraft()
    setCreateProvider(providerKey)
    startCreate()
  }

  const handleSelectConnection = (connectionId) => {
    captureActiveDraft()
    selectConnection(connectionId)
  }

  // Cancel is the draft's explicit discard; switching rows preserves instead.
  const handleCancelCreate = () => {
    // Every other transition unmounts one form and mounts another, and the new
    // one reports dirty=false on its first Formik render. Cancel can leave NO
    // form mounted (an org whose only pending work was this draft), so nothing
    // would clear the flag and the modal would warn about input that no longer
    // exists. A restored draft re-arms the guard through `drafts`, not through
    // this flag, so clearing it here cannot under-report.
    formDirtyRef.current = false
    removeDraft(createProvider)
    cancelCreate()
  }

  // Same shell element in every branch so React keeps the AnimatedHeight
  // instance (and its measured height) across the loading -> loaded swap —
  // that swap is exactly the height change being animated. The frame carries
  // the modal's minimum height so the dialog opens at a stable size instead
  // of a loader-sized bar.
  const panelFrame = (content) => (
    <AnimatedHeight className="connection-panel-shell">
      <div className="connection-panel-frame">{content}</div>
    </AnimatedHeight>
  )

  if (!loaded) {
    return panelFrame(
      <div className="connection-panel-frame__filler">
        <Loader size="large" centered label="Loading connections…" />
      </div>
    )
  }

  if (loadError) {
    return panelFrame(
      <div className="connection-panel-frame__filler">
        <ErrorMessage content="Could not load the knowledge base connections. Close and reopen to retry." />
      </div>
    )
  }

  const addDropdown = (
    <ActionsDropdown
      triggerLabel="Add"
      dataTestId="knowledge-base-connections-add"
      actions={KNOWLEDGE_BASE_PROVIDERS.map((provider) => ({
        label: provider.label,
        icon: provider.icon,
        testId: provider.addTestId,
        disabled: !isNil(provider.addDisabledReason(connections)),
        disabledTooltip: provider.addDisabledReason(connections),
        // Reopens the provider's preserved draft when one exists, so Add can
        // never silently replace typed input with a blank form.
        onClick: () => openDraft(provider.key),
      }))}
    />
  )

  // `onSaved(newId)` after a create; no argument everywhere else, which keeps
  // the current selection. A successful create resolves its draft; a failed
  // one passes no id and keeps the draft for a retry.
  const handleSaved = (preferredId) => {
    if (creating && !isNil(preferredId)) {
      removeDraft(createProvider)
      setScanNoticeId(preferredId)
    }
    return reload(() => true, preferredId)
  }

  const confirmErase = async () => {
    setPurging(true)
    const purged = await dispatch(
      providerOf(eraseTarget.provider).purgeDocuments({ connectionId: eraseTarget.id })
    )
    setPurging(false)
    if (isNil(purged)) {
      // The thunk already toasted it; nothing was erased server-side, so keep
      // the confirmation open for a retry.
      return
    }
    setEraseTarget(null)
    await reload()
  }

  const eraseButton = isNil(connection) ? null : (
    <Button
      type="button"
      critical
      // agent-gpt refuses the purge with 409 while the connection is live, so
      // don't offer it — say why instead of letting the operator earn an error.
      disabled={connection.active}
      disabledTooltip="Pause syncing before erasing its documents"
      onClick={() => setEraseTarget(connection)}
      data-testid="knowledge-base-erase-documents-button"
    >
      Erase documents
    </Button>
  )

  // Only when there is genuinely nothing: a preserved draft belongs in the rail
  // below, or it becomes unreachable while still arming the modal's
  // unsaved-changes guard.
  if (isEmpty(connections) && !creating && isEmpty(drafts)) {
    return panelFrame(
      <div className="connection-panel-frame__filler">
        <NoData
          headerText="No knowledge base connections yet"
          padded
          data-testid="knowledge-base-connections-empty"
        >
          <p>
            Connect a SharePoint tenant or a Google Drive workspace to index its documents into this
            organization’s knowledge base.
          </p>
          <div className="mt">{addDropdown}</div>
        </NoData>
      </div>
    )
  }

  // Cancel returns to whatever is left behind — a saved connection, or another
  // provider's preserved draft. With neither, there is nowhere to go back to
  // and the button closes the modal instead.
  const returnsToTheList =
    !isEmpty(connections) ||
    KNOWLEDGE_BASE_PROVIDERS.some(
      (provider) => provider.key !== createProvider && !isNil(drafts[provider.key])
    )

  // Null while a draft is parked on an org with no saved connections: the rail
  // has rows to offer but nothing is selected.
  const detailProvider = creating
    ? providerOf(createProvider)
    : connection && providerOf(connection.provider)
  const DetailForm = detailProvider?.FormComponent
  const detail = isNil(detailProvider) ? (
    <p className="field-help small-text" data-testid="knowledge-base-no-selection">
      Select an unsaved connection to pick up where you left off, or add another.
    </p>
  ) : (
    <DetailForm
      // AbstractForm has no `enableReinitialize`: remount per selection — and
      // per provider, so the two create forms never share an instance — or the
      // form keeps the previously selected connection's values.
      key={creating ? `new:${detailProvider.key}` : `${connection.provider}:${connection.id}`}
      organizationId={organizationId}
      connection={connection}
      syncState={syncState}
      onSaved={handleSaved}
      // Cancelling a create form discards its draft and goes back to the list
      // rather than closing the whole modal.
      onClose={creating && returnsToTheList ? handleCancelCreate : onClose}
      closeLabel={creating && returnsToTheList ? 'Cancel' : 'Close'}
      footerActions={eraseButton}
      onDirty={handleFormDirty}
      onFormikReady={handleFormikState}
      dismissedNotebooks={creating ? [] : dismissedNotebooks[connection.id]}
      onDismissedNotebooksChange={(keys) => changeDismissedNotebooks(connection.id, keys)}
      playbooks={playbooks}
      playbooksLoading={playbooksLoading}
      playbooksFailed={playbooksFailed}
      draftValues={creating ? drafts[createProvider] : undefined}
      // Passed down because the forms cannot import the registry that imports them.
      supportsManageFiles={detailProvider.supportsManageFiles}
    />
  )

  const draftRows = KNOWLEDGE_BASE_PROVIDERS.filter(
    (provider) => !isNil(drafts[provider.key]) || (creating && createProvider === provider.key)
  ).map((provider) => ({
    key: provider.key,
    name: `New ${provider.label} connection`,
    icon: provider.icon,
    selected: creating && createProvider === provider.key,
    onSelect: () => openDraft(provider.key),
  }))

  const EraseConfirmModal = isNil(eraseTarget)
    ? null
    : providerOf(eraseTarget.provider).EraseConfirmModal

  return panelFrame(
    <>
      <OrgSyncPausedBanner connections={connections} />
      <div className="connection-panel">
        <ConnectionList
          connections={connections}
          selectedId={selectedId}
          onSelect={handleSelectConnection}
          addControl={addDropdown}
          creating={creating}
          drafts={draftRows}
          primaryName={(row) => providerOf(row.provider).connectionName(row)}
          identifier={(row) => providerOf(row.provider).identifier(row)}
          rowIcon={(row) => providerOf(row.provider).icon}
          suggestionCount={(row) =>
            providerOf(row.provider).suggestionCount(row, dismissedNotebooks[row.id])
          }
          alertLabel={(row) => providerOf(row.provider).alertLabel(row)}
          testId="knowledge-base-connections"
        />
        <div className="connection-panel__detail" data-testid="knowledge-base-connection-detail">
          {detail}
        </div>
        {!isNil(eraseTarget) && (
          <EraseConfirmModal
            connection={eraseTarget}
            siblingCount={
              connections.filter((row) => row.provider === eraseTarget.provider).length - 1
            }
            purging={purging}
            onConfirm={confirmErase}
            onClose={() => {
              if (!purging) {
                setEraseTarget(null)
              }
            }}
          />
        )}
        {connections.find((row) => row.id === scanNoticeId)?.review_state ===
          REVIEW_STATE.SCANNING && <ScanningModal onClose={() => setScanNoticeId(null)} />}
      </div>
    </>
  )
}
